import { HOME_URL, LOGIN_URL } from "@/config";
import { getFlatMenuList } from "@/utils";
import type { RouteRecordRaw } from "vue-router";

/**
 * layout布局下的路由
 * @description 1. `KeepAlive`不支持多级路由，需要扁平化路由，但代码保持嵌套结构，所以单独处理.
 * 2. 列表页使用`KeepAlive`之后，详情页必须用`${列表name}-id`，不然进入非详情页无法清除`KeepAlive`刷新页面.
 * 3. 清除`KeepAlive`在`@/layouts/index`的`onBeforeRouteUpdate`统一处理.
 */
const layoutRoutes: RouteRecordRaw[] = [
	{
		path: HOME_URL,
		name: "home",
		component: () => import("@/views/home/index.vue"),
		meta: {
			title: "home",
			hiddentLayoutHeader: true
		},
		children: [
			{
				path: "/bulletin/:id",
				name: "bulletin-id",
				component: () => import("@/views/bulletin/id/index.vue"),
				meta: {
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			}
		]
	},
	{
		path: "/starting",
		name: "starting",
		component: () => import("@/views/starting/index.vue"),
		meta: {
			title: "starting"
		}
	},
	{
		path: "/records",
		name: "records",
		component: () => import("@/views/records/index.vue"),
		meta: {
			title: "records",
			hiddentLayoutHeader: true
		}
	},
	{
		path: "/my",
		name: "my",
		component: () => import("@/views/my/index.vue"),
		meta: {
			title: "my",
			hiddentLayoutHeader: true
		}
	},
	{
		path: "/event",
		name: "event",
		component: () => import("@/views/event/index.vue"),
		meta: {
			title: "event",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/gifts",
		name: "gifts",
		component: () => import("@/views/gifts/index.vue"),
		meta: {
			title: "gifts",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true,
			isKeepAlive: true
		},
		children: [
			{
				path: "/gifts/:id",
				name: "gifts-id",
				component: () => import("@/views/gifts/id/index.vue"),
				meta: {
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			},
			{
				path: "/gifts/records",
				name: "gifts-records",
				component: () => import("@/views/gifts/records/index.vue"),
				meta: {
					title: "giftsRecord",
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			}
		]
	},
	{
		path: "/withdraw",
		name: "withdraw",
		component: () => import("@/views/withdraw/index.vue"),
		meta: {
			title: "withdraw",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/deposit",
		name: "deposit",
		component: () => import("@/views/deposit/index.vue"),
		meta: {
			title: "deposit",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/tc",
		name: "tc",
		component: () => import("@/views/tc/index.vue"),
		meta: {
			title: "tc",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/certificate",
		name: "certificate",
		component: () => import("@/views/certificate/index.vue"),
		meta: {
			title: "certificate"
		}
	},
	{
		path: "/faqs",
		name: "faqs",
		component: () => import("@/views/faqs/index.vue"),
		meta: {
			title: "faqs",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/about",
		name: "about",
		component: () => import("@/views/about/index.vue"),
		meta: {
			title: "about",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/vip",
		name: "vip",
		component: () => import("@/views/vip/index.vue"),
		meta: {
			title: "vip",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/transaction",
		name: "transaction",
		component: () => import("@/views/transaction/index.vue"),
		meta: {
			title: "transaction",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/profileItem",
		name: "profileItem",
		component: () => import("@/views/profileItem/index.vue"),
		meta: {
			title: "profileItem.title",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		},
		children: [
			{
				path: "/profileItem/profile",
				name: "profileItem-profile",
				component: () => import("@/views/profileItem/profile/index.vue"),
				meta: {
					title: "profileItem.profile",
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			},
			{
				path: "/profileItem/updatePassword",
				name: "profileItem-updatePassword",
				component: () => import("@/views/profileItem/updatePassword/index.vue"),
				meta: {
					title: "profileItem.updatePassword",
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			},
			{
				path: "/profileItem/updateTransactionPassword",
				name: "profileItem-updateTransactionPassword",
				component: () => import("@/views/profileItem/updateTransactionPassword/index.vue"),
				meta: {
					title: "profileItem.updateTransactionPassword",
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			}
		]
	},
	{
		path: "/paymentMethods",
		name: "paymentMethods",
		component: () => import("@/views/paymentMethods/index.vue"),
		meta: {
			title: "paymentMethods",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		},
		children: [
			{
				path: "/paymentMethods/:id",
				name: "paymentMethods-id",
				component: () => import("@/views/paymentMethods/id/index.vue"),
				meta: {
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			}
		]
	},
	{
		path: "/notice",
		name: "notice",
		component: () => import("@/views/notice/index.vue"),
		meta: {
			title: "notice",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		},
		children: [
			{
				path: "/notice/:id",
				name: "notice-id",
				component: () => import("@/views/notice/id/index.vue"),
				meta: {
					hiddentLayoutHeader: true,
					hiddentLayoutFooter: true
				}
			}
		]
	},
	{
		path: "/hotMusic",
		name: "hotMusic",
		component: () => import("@/views/hotMusic/index.vue"),
		meta: {
			title: "hotMusic",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/hotSongCharts",
		name: "hotSongCharts",
		component: () => import("@/views/hotSongCharts/index.vue"),
		meta: {
			title: "hotSongCharts",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/bonusRules",
		name: "bonusRules",
		component: () => import("@/views/bonusRules/index.vue"),
		meta: {
			title: "bonusRules",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/order",
		name: "order",
		component: () => import("@/views/order/index.vue"),
		meta: {
			title: "order",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/wheels",
		name: "wheels",
		component: () => import("@/views/wheels/index.vue"),
		meta: {
			title: "wheels",
			hiddentLayoutHeader: true,
			hiddentLayoutFooter: true
		}
	},
	{
		path: "/customerServices",
		name: "customerServices",
		component: () => import("@/views/customerServices/index.vue"),
		meta: {
			title: "customerServices",
			hiddentLayoutHeader: true
		}
	}
];

const routes = getFlatMenuList(layoutRoutes);
/**
 * staticRouter (静态路由)
 */
export const staticRouter: RouteRecordRaw[] = [
	{
		path: "/",
		redirect: HOME_URL
	},
	{
		path: LOGIN_URL,
		name: "login",
		component: () => import("@/views/login/index.vue"),
		meta: {
			title: "login"
		}
	},
	// {
	// 	path: "/start",
	// 	name: "start",
	// 	component: () => import("@/views/start/index.vue"),
	// 	meta: {
	// 		title: "start"
	// 	}
	// },
	{
		path: "/register",
		name: "register",
		component: () => import("@/views/register/index.vue"),
		meta: {
			title: "register"
		}
	},
	{
		path: "/privacyPolicy",
		name: "privacyPolicy",
		component: () => import("@/views/privacyPolicy/index.vue"),
		meta: {
			title: "privacyPolicy"
		}
	},
	{
		path: "/deficonnect",
		name: "deficonnect",
		component: () => import("@/views/deficonnect/index.vue"),
		meta: {
			title: "deficonnect"
		}
	},
	{
		path: "/deficonnect/:id",
		name: "deficonnect-id",
		component: () => import("@/views/deficonnect/id/index.vue"),
		meta: {}
	},
	{
		path: "/registrationAgreement",
		name: "registrationAgreement",
		component: () => import("@/views/registrationAgreement/index.vue"),
		meta: {
			title: "registrationAgreement"
		}
	},
	{
		path: "/layout",
		name: "layout",
		component: () => import("@/layouts/index.vue"),
		redirect: HOME_URL,
		children: routes
	}
];

/**
 * errorRouter (错误页面路由)
 */
export const errorRouter = [
	{
		path: "/404",
		name: "404",
		component: () => import("@/components/ErrorMessage/404.vue"),
		meta: {
			title: "404"
		}
	},
	// Resolve refresh page, route warnings
	{
		path: "/:pathMatch(.*)*",
		component: () => import("@/components/ErrorMessage/404.vue")
	}
];
